/**
 * Shared rental-duration billing math (mirrors `RentalDurationBilling.cs`):
 * full cycles at the period rate, remainder days prorated — unless the
 * remainder hits the rollup threshold (3 days → full week, 21 days → full month).
 */

export const WEEKLY_DURATION_THRESHOLD_DAYS = 21;
export const DAYS_PER_WEEK = 7;
export const DEFAULT_BILLING_CYCLE_DAYS = 28;
/** Matches server `BillingConstants.DefaultWeeklyMultiplier` (monthly rate → weekly rate). */
export const MONTHLY_TO_WEEKLY_MULTIPLIER = 0.333333;
/** Remainder days at or above this count roll up to a full week. */
export const WEEKLY_REMAINDER_ROLLUP_DAYS = 3;
/** Remainder days at or above this count roll up to a full month. */
export const MONTHLY_REMAINDER_ROLLUP_DAYS = 21;

export function isWeeklyDuration(duration: number): boolean {
  return duration > 0 && duration < WEEKLY_DURATION_THRESHOLD_DAYS;
}

/**
 * Multiplier against a single-cycle (week or month) rate.
 * Full cycles bill at full rate; remainder days are prorated unless
 * `remainderRollupDays` is reached, in which case the remainder bills as
 * one more full cycle.
 */
export function getCycleMultiplier(duration: number, cycleDays: number, remainderRollupDays: number): number {
  if (duration <= 0 || cycleDays <= 0) {
    return 0;
  }

  const fullCycles = Math.floor(duration / cycleDays);
  const remainder = duration % cycleDays;

  if (remainder === 0) {
    return fullCycles;
  }

  if (remainder >= remainderRollupDays) {
    return fullCycles + 1;
  }

  return fullCycles + remainder / cycleDays;
}

export function getWeeklyMultiplier(duration: number): number {
  return getCycleMultiplier(duration, DAYS_PER_WEEK, WEEKLY_REMAINDER_ROLLUP_DAYS);
}

export function getMonthlyMultiplier(duration: number, billingCycleDays = DEFAULT_BILLING_CYCLE_DAYS): number {
  return getCycleMultiplier(duration, billingCycleDays, MONTHLY_REMAINDER_ROLLUP_DAYS);
}

/**
 * Period multiplier used for Rent and attachments.
 * Under 21 days: weekly cycles of 7 days; remainder ≥ 3 days bills a full extra
 * week, otherwise prorate on 7 days.
 * 21+ days: monthly cycles of 28 days; remainder ≥ 21 days bills a full extra
 * month, otherwise prorate on 28 days.
 */
export function getRentalPeriodMultiplier(duration: number, billingCycleDays = DEFAULT_BILLING_CYCLE_DAYS): number {
  if (isWeeklyDuration(duration)) {
    return getWeeklyMultiplier(duration);
  }
  return getMonthlyMultiplier(duration, billingCycleDays);
}
